import { useEffect, useState } from "react";
import EventLayout from "@/components/event/EventLayout";
import PdfFlipbook from "@/components/event/PdfFlipbook";
import EventAgenda from "@/components/event/EventAgenda";
import { BACKEND_URL } from "@/lib/api";
import { eventApi } from "@/lib/eventHub";
import {
  BookOpen, Sparkles, ArrowUpRight, FileText, Download, Layers, BookOpenCheck,
} from "lucide-react";

export default function Program() {
  const [booklet, setBooklet] = useState(null);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(null); // {view, doc, title, toc}

  useEffect(() => {
    let m = true;
    (async () => {
      try {
        const res = await fetch(`${BACKEND_URL}/api/event/program-booklet`);
        const data = await res.json();
        if (m) setBooklet(data.booklet);
      } catch { /* noop */ }
      if (m) setLoading(false);
    })();
    eventApi.track("program_view");
    return () => { m = false; };
  }, []);

  const openBook = (view, startPage = null) => {
    if (!booklet) return;
    const title = view === "friday"
      ? booklet.subtitle_friday
      : view === "saturday"
        ? booklet.subtitle_saturday
        : booklet.title;
    const pdf_url = view === "friday"
      ? booklet.friday_pdf_url
      : view === "saturday"
        ? booklet.saturday_pdf_url
        : booklet.full_pdf_url;
    const size = view === "friday" ? booklet.friday_size : view === "saturday" ? booklet.saturday_size : booklet.full_size;
    const pages = view === "friday" ? booklet.friday_pages.length : view === "saturday" ? booklet.saturday_pages.length : booklet.page_count;
    const toc = view === "friday" ? (booklet.toc_friday || [])
      : view === "saturday" ? (booklet.toc_saturday || [])
        : (booklet.toc_full || []);

    setOpen({
      title,
      pdf_url,
      file_size: size,
      page_count: pages,
      filename_original: booklet.filename_original,
      toc,
      view,
      startPage,
    });
    eventApi.track("program_open", { view, start_page: startPage });
  };

  const downloadFull = () => {
    if (!booklet) return;
    const url = `${BACKEND_URL}${booklet.full_pdf_url}&download=1`;
    const a = document.createElement("a");
    a.href = url;
    a.download = "business-showers-2026-combined.pdf";
    a.click();
    eventApi.track("program_download", { view: "full" });
  };

  return (
    <EventLayout showProgramBanner={false} className="bg-gradient-to-b from-white via-emerald-50/40 to-white">
      <div className="mx-auto max-w-lg px-5 py-8">
        {/* Header */}
        <div className="mb-6">
          <div className="inline-flex items-center gap-1.5 rounded-full bg-fuchsia-50 border border-fuchsia-200 px-3 py-1 mb-3">
            <BookOpen size={12} className="text-fuchsia-700" />
            <span className="text-[10px] tracking-[0.22em] uppercase font-[Outfit] font-bold text-fuchsia-800">
              Program Library
            </span>
          </div>
          <h1 className="font-[Cormorant_Garamond] text-4xl sm:text-5xl leading-[1.02] text-emerald-950">
            The <span className="italic text-fuchsia-700">Fast Forward</span> Program
          </h1>
          <p className="mt-3 font-[Outfit] text-sm text-emerald-950/60 leading-relaxed">
            Two program booklets — one for each night. Tap a day to open its flipbook. Pinch to zoom, swipe to turn pages.
          </p>
        </div>

        {loading && (
          <div className="text-center py-16 font-[Outfit] text-emerald-950/50 text-sm">
            Loading program booklet…
          </div>
        )}

        {!loading && !booklet && (
          <div
            data-testid="program-empty"
            className="rounded-3xl border-2 border-dashed border-emerald-950/15 bg-white p-10 text-center"
          >
            <FileText size={40} className="mx-auto text-emerald-950/30 mb-3" />
            <div className="font-[Cormorant_Garamond] text-2xl text-emerald-950">Program booklet coming soon</div>
            <div className="text-xs text-emerald-950/50 mt-2 font-[Outfit]">
              Administrators can upload the combined booklet from the Admin dashboard.
            </div>
          </div>
        )}

        {!loading && booklet && (
          <div data-testid="program-library" className="space-y-5">
            <BookletCard
              onOpen={() => openBook("friday")}
              coverSrc={`${BACKEND_URL}${booklet.cover_url}`}
              nightLabel="Night One · VIP"
              subtitle={booklet.subtitle_friday}
              title="Friday, July 17"
              pageCount={booklet.friday_pages.length}
              fileSize={booklet.friday_size}
              accent="fuchsia"
              testid="program-booklet-friday"
            />
            <BookletCard
              onOpen={() => openBook("saturday")}
              coverSrc={`${BACKEND_URL}${booklet.cover_url}`}
              nightLabel="Main Event"
              subtitle={booklet.subtitle_saturday}
              title="Saturday, July 18"
              pageCount={booklet.saturday_pages.length}
              fileSize={booklet.saturday_size}
              accent="emerald"
              testid="program-booklet-saturday"
            />

            {/* Quick-jump ToC — appears when the booklet has a Table of Contents */}
            {(booklet.toc_full || []).length > 0 && (
              <div
                data-testid="program-toc-preview"
                className="rounded-2xl border-2 border-emerald-100 bg-white p-4"
              >
                <div className="flex items-center gap-2 mb-3">
                  <span className="inline-flex h-8 w-8 rounded-lg bg-fuchsia-100 text-fuchsia-700 items-center justify-center">
                    <BookOpenCheck size={14} />
                  </span>
                  <div>
                    <div className="text-[10px] tracking-[0.22em] uppercase font-[Outfit] font-bold text-fuchsia-700">Jump To</div>
                    <div className="font-[Cormorant_Garamond] text-lg leading-tight text-emerald-950">Table of Contents</div>
                  </div>
                </div>
                <div className="grid grid-cols-1 gap-1.5">
                  {booklet.toc_full.map((e, i) => {
                    // Determine which day's view contains this original page
                    const inFriday = (booklet.friday_pages || []).includes(
                      // reverse-lookup original page from curated ToC (which stores curated original pages)
                      e.page
                    );
                    const inSaturday = (booklet.saturday_pages || []).includes(e.page);
                    // Prefer opening in Saturday view (main event) — falls back to Friday, then full
                    const view = inSaturday ? "saturday" : inFriday ? "friday" : "full";
                    const targetToc = view === "saturday" ? booklet.toc_saturday : view === "friday" ? booklet.toc_friday : booklet.toc_full;
                    // Find the remapped page number in the chosen view's ToC (matching by title)
                    const remapped = (targetToc || []).find((t) => t.title === e.title);
                    const targetPage = remapped ? remapped.page : e.page;
                    return (
                      <button
                        key={`${e.title}-${i}`}
                        onClick={() => openBook(view, targetPage)}
                        data-testid={`toc-chip-${i}`}
                        className="w-full text-left px-3 py-2.5 rounded-xl border border-emerald-100 bg-emerald-50/40 hover:border-fuchsia-400 hover:bg-fuchsia-50 flex items-center gap-3 transition-colors group"
                      >
                        <span className="text-[10px] font-mono font-bold text-emerald-950/40 group-hover:text-fuchsia-700 shrink-0 min-w-[24px]">
                          p{e.page}
                        </span>
                        <span className="flex-1 text-sm font-[Outfit] text-emerald-950 truncate">
                          {e.title}
                        </span>
                        <span className={`text-[9px] font-[Outfit] font-bold uppercase tracking-widest px-1.5 py-0.5 rounded-full shrink-0 ${
                          view === "friday" ? "bg-fuchsia-100 text-fuchsia-700"
                            : view === "saturday" ? "bg-emerald-100 text-emerald-700"
                              : "bg-slate-100 text-slate-700"
                        }`}>
                          {view === "friday" ? "Fri" : view === "saturday" ? "Sat" : "Full"}
                        </span>
                        <ArrowUpRight size={13} className="text-emerald-950/30 group-hover:text-fuchsia-600 shrink-0" />
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Combined 2-day download */}
            <div
              data-testid="program-combined"
              className="rounded-2xl border-2 border-emerald-950 bg-emerald-950 text-white p-5"
            >
              <div className="flex items-start gap-3">
                <span className="h-11 w-11 rounded-xl bg-white/10 border border-white/20 flex items-center justify-center shrink-0">
                  <Layers size={20} className="text-fuchsia-300" />
                </span>
                <div className="flex-1 min-w-0">
                  <div className="text-[10px] tracking-[0.22em] uppercase font-[Outfit] font-bold text-fuchsia-300">
                    Combined 2-Day Booklet
                  </div>
                  <div className="font-[Cormorant_Garamond] text-xl leading-tight mt-1">
                    {booklet.title}
                  </div>
                  <div className="text-[11px] font-[Outfit] text-white/60 mt-1">
                    {booklet.page_count} pages · {(booklet.full_size / (1024 * 1024)).toFixed(1)} MB
                  </div>
                </div>
              </div>
              <div className="mt-4 grid grid-cols-2 gap-2">
                <button
                  onClick={() => openBook("full")}
                  data-testid="program-open-combined"
                  className="rounded-xl h-11 bg-white/10 hover:bg-white/20 border border-white/20 text-white font-[Outfit] font-bold text-sm flex items-center justify-center gap-2 transition-colors"
                >
                  <BookOpen size={14} /> Open
                </button>
                <button
                  onClick={downloadFull}
                  data-testid="program-download-combined"
                  className="rounded-xl h-11 bg-fuchsia-500 hover:bg-fuchsia-600 text-white font-[Outfit] font-bold text-sm flex items-center justify-center gap-2 shadow-lg shadow-fuchsia-500/30"
                >
                  <Download size={14} /> Download
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Reassuring note */}
        <div className="mt-8 rounded-2xl bg-white border border-emerald-100 p-5">
          <div className="flex items-start gap-3">
            <Sparkles size={16} className="text-fuchsia-600 shrink-0 mt-0.5" />
            <div>
              <div className="text-[10px] tracking-[0.22em] uppercase font-[Outfit] font-bold text-fuchsia-700">Always with you</div>
              <div className="font-[Cormorant_Garamond] text-xl leading-tight mt-1 text-emerald-950">Your program is always available here.</div>
              <div className="text-xs font-[Outfit] text-emerald-950/60 mt-1">
                Add this app to your phone&rsquo;s home screen so the program is one tap away all weekend.
              </div>
            </div>
          </div>

          {/* Text-based two-day agenda (spec §4 · restore full program) */}
          <div className="pt-2">
            <EventAgenda title="Full two-day agenda" />
          </div>
        </div>
      </div>

      {open && <PdfFlipbook doc={open} toc={open.toc || []} startPage={open.startPage} onClose={() => setOpen(null)} />}
    </EventLayout>
  );
}

// ─── Booklet card ────────────────────────────────────────────────────────────
function BookletCard({ onOpen, coverSrc, nightLabel, subtitle, title, pageCount, fileSize, accent, testid }) {
  const isDark = accent === "fuchsia";
  const accentText = isDark ? "text-fuchsia-300" : "text-emerald-300";
  const ringGlow = isDark ? "hover:shadow-fuchsia-600/40" : "hover:shadow-emerald-700/40";

  return (
    <button
      onClick={onOpen}
      data-testid={testid}
      className={`group relative w-full text-left rounded-3xl overflow-hidden bg-emerald-950 shadow-lg transition-all duration-300 hover:-translate-y-1 hover:shadow-2xl ${ringGlow}`}
    >
      <div className="relative aspect-[4/5] sm:aspect-[3/4] overflow-hidden">
        <img
          src={coverSrc}
          alt={`Cover: ${title}`}
          className="absolute inset-0 h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
          loading="lazy"
        />
        <div className="absolute inset-x-0 bottom-0 h-3/5 bg-gradient-to-t from-emerald-950 via-emerald-950/70 to-transparent" />

        {/* Night badge */}
        <div className={`absolute top-4 left-4 inline-flex items-center gap-1.5 rounded-full ${isDark ? "bg-fuchsia-600" : "bg-emerald-600"} px-3 py-1.5 shadow-lg`}>
          <span className="text-[10px] tracking-[0.2em] uppercase font-[Outfit] font-bold text-white">
            {nightLabel}
          </span>
        </div>

        {/* Pages badge */}
        <div className="absolute top-4 right-4 rounded-full bg-white/15 backdrop-blur px-3 py-1.5 border border-white/20">
          <span className="text-[10px] tracking-[0.16em] uppercase font-[Outfit] font-bold text-white flex items-center gap-1">
            <BookOpen size={11} /> {pageCount} pages
          </span>
        </div>

        <div className="absolute inset-x-0 bottom-0 p-5 sm:p-6">
          <div className={`text-[10px] tracking-[0.22em] uppercase font-[Outfit] font-bold ${accentText} mb-2`}>
            {subtitle}
          </div>
          <h3 className="font-[Cormorant_Garamond] text-2xl sm:text-3xl leading-[1.05] text-white">
            {title}
          </h3>
          <div className="mt-4 flex items-center justify-between">
            <div className="inline-flex items-center gap-2 rounded-full bg-white text-emerald-950 px-4 py-2 font-[Outfit] font-bold text-sm shadow-lg">
              Open Booklet <ArrowUpRight size={14} />
            </div>
            <div className="text-[10px] font-[Outfit] font-semibold text-white/50 uppercase tracking-widest">
              {(fileSize / (1024 * 1024)).toFixed(1)} MB
            </div>
          </div>
        </div>
      </div>
    </button>
  );
}
